import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { CharacterActor, CHARACTER_PRESETS, type CharacterSpec, type ReactionName, type Speech } from "./visuals-character";
import { Caption } from "./prims";
import { easeOutQuint } from "./anim";
import { cameraAt, cameraTransformCentered, focusBox, type CameraTrack } from "./camera";
import { DustPuff, SpeedLines, ImpactStar } from "./effects";
import { SetLayer, deriveSet, shade, type SetKind } from "./sets";
import type { Beat } from "./director";
import type { SceneDef } from "./scenes";

/**
 * Anivaze v4 — STORYLINE stage compositor (STORYLINE.md §4).
 *
 * Composes ONE story beat: a background plate + a cast of vector characters
 * placed on the stage + speech bubbles carrying their dialogue. Pure function of
 * `t` (characters animate via their reactions; an image background gets a gentle
 * Ken-Burns). This is what turns the puppet rig into a narrated skit.
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (x: number) => { const c = clamp01(x); return c * c * (3 - 2 * c); };

export interface StoryCastMember {
  charId: string;
  spec: CharacterSpec;
  /** Stage placement, normalized in the box: x/y in 0..1, scale multiplies size. */
  stage: { x: number; y: number; scale: number };
  reaction: ReactionName;
  /** Performance-acting timeline — the member acts each phrase (REAL-ANIMATION §1). */
  acting?: ActingKey[];
  facing?: -1 | 1;
  dialogue?: string;
  /** Lip-sync window (REAL-ANIMATION §5) — usually the beat's NARRATION paced
   *  over the beat, so the mouth articulates as long as the voiceover speaks. */
  speech?: Speech;
  /** Walk in from offstage at the start of the beat (the film-opening entrance). */
  walkIn?: boolean;
}

export interface StoryBeat {
  background:
    | { kind: "flat"; sky?: string; ground?: string; set?: SetKind }
    | { kind: "image"; href: string };
  cast: StoryCastMember[];
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars && cur) { lines.push(cur); cur = w; }
    else cur = (cur + " " + w).trim();
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 4);
}

function Bubble({ x, y, text, theme, u, appear }: { x: number; y: number; text: string; theme: Theme; u: number; appear: number }) {
  const P = theme.palette;
  const maxChars = 26;
  const lines = wrapText(text, maxChars);
  const fs = u * 2.0;
  const lh = fs * 1.22;
  const w = Math.min(u * 40, Math.max(u * 14, (Math.min(maxChars, Math.max(...lines.map((l) => l.length))) + 2) * fs * 0.56));
  const h = lines.length * lh + u * 2.4;
  const bx = Math.max(u, x - w / 2);
  const by = y - h;
  const s = 0.7 + 0.3 * appear;
  return (
    <g opacity={appear} transform={`translate(${x} ${y}) scale(${s.toFixed(3)}) translate(${-x} ${-y})`}>
      <rect x={bx} y={by} width={w} height={h} rx={u * 1.6} fill={P.surface2} stroke={P.primary} strokeWidth={u * 0.18} />
      <path d={`M ${x - u * 1.2} ${by + h - u * 0.2} L ${x} ${by + h + u * 1.8} L ${x + u * 1.2} ${by + h - u * 0.2} Z`} fill={P.surface2} stroke={P.primary} strokeWidth={u * 0.18} strokeLinejoin="round" />
      <rect x={x - u * 1.4} y={by + h - u * 0.7} width={u * 2.8} height={u} fill={P.surface2} />
      <text x={bx + w / 2} y={by + u * 1.9} textAnchor="middle" fontFamily={theme.title.family} fontWeight={600} fontSize={fs} fill={P.ink}>
        {lines.map((l, i) => <tspan key={i} x={bx + w / 2} dy={i === 0 ? 0 : lh}>{l}</tspan>)}
      </text>
    </g>
  );
}

function Background({ beat, box, t }: { beat: StoryBeat; box: Box; t: number }) {
  if (beat.background.kind === "image") {
    // gentle Ken-Burns: slow zoom + drift
    const z = 1.06 + 0.05 * Math.sin(t * 0.2);
    const dx = Math.sin(t * 0.15) * box.w * 0.01;
    // Unique clip id so crossfading film layers don't collide on one <clipPath>.
    let h = 0; const s = beat.background.href; for (let i = 0; i < Math.min(s.length, 64); i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    const clipId = `story-bg-${h.toString(36)}`;
    return (
      <g>
        <clipPath id={clipId}><rect x={box.x} y={box.y} width={box.w} height={box.h} /></clipPath>
        <g clipPath={`url(#${clipId})`}>
          <image href={beat.background.href} x={box.x} y={box.y} width={box.w} height={box.h}
            preserveAspectRatio="xMidYMid slice"
            transform={`translate(${box.cx + dx} ${box.cy}) scale(${z}) translate(${-box.cx} ${-box.cy})`} />
        </g>
      </g>
    );
  }
  const sky = beat.background.sky ?? "#cfe4ff";
  const ground = beat.background.ground ?? "#bfe3b0";
  const gy = box.y + box.h * 0.74;
  // MULTIPLANE PARALLAX (the Disney multiplane trick, pure f(t)): a slow camera
  // sway; each plane offsets at its own depth rate — sky barely, hills a little,
  // ground the most (characters ride the front plane). The differential motion
  // is what reads as DEPTH. Plus drifting clouds and rising atmosphere motes.
  const camX = Math.sin(t * 0.07) * box.w * 0.02;
  const hill = shade(ground, 0.82);
  const hillFar = shade(ground, 0.9);
  const cloudY = [0.14, 0.22, 0.1];
  const cloudSp = [0.011, 0.017, 0.008];
  return (
    <g>
      {/* Plates span 1.24w so the close-up camera pan (deriveShots, zoom ≤1.55
          around stage.x 0.72) never sees past their edge. */}
      <rect x={box.x - box.w * 0.12} y={box.y} width={box.w * 1.24} height={box.h} fill={sky} />
      {/* sun/moon — deepest plane, barely moves */}
      <g transform={`translate(${camX * 0.12} 0)`}>
        <ellipse cx={box.x + box.w * 0.8} cy={box.y + box.h * 0.15} rx={box.h * 0.07} ry={box.h * 0.07} fill="#fff" opacity={0.28} />
        <ellipse cx={box.x + box.w * 0.8} cy={box.y + box.h * 0.15} rx={box.h * 0.05} ry={box.h * 0.05} fill="#fff" opacity={0.8} />
      </g>
      {/* clouds — drift on their own + shallow parallax */}
      <g transform={`translate(${camX * 0.22} 0)`} opacity={0.5}>
        {cloudY.map((cy, i) => {
          const span = box.w * 1.3;
          const cx = box.x - box.w * 0.15 + ((t * cloudSp[i] * box.w + i * span * 0.37) % span);
          const y = box.y + box.h * cy;
          const r = box.h * (0.028 + 0.012 * i);
          return (
            <g key={i} fill="#ffffff">
              <ellipse cx={cx} cy={y} rx={r * 2.4} ry={r} />
              <ellipse cx={cx - r * 1.6} cy={y + r * 0.35} rx={r * 1.5} ry={r * 0.75} />
              <ellipse cx={cx + r * 1.7} cy={y + r * 0.3} rx={r * 1.6} ry={r * 0.8} />
            </g>
          );
        })}
      </g>
      {/* far hills — mid plane */}
      <g transform={`translate(${camX * 0.45} 0)`}>
        <path d={`M ${box.x - box.w * 0.1} ${gy} Q ${box.x + box.w * 0.22} ${gy - box.h * 0.11} ${box.x + box.w * 0.5} ${gy} Z`} fill={hillFar} />
        <path d={`M ${box.x + box.w * 0.35} ${gy} Q ${box.x + box.w * 0.68} ${gy - box.h * 0.14} ${box.x + box.w * 1.1} ${gy} Z`} fill={hill} />
      </g>
      {/* ground — front plane, moves the most */}
      <g transform={`translate(${camX * 0.9} 0)`}>
        <rect x={box.x - box.w * 0.12} y={gy} width={box.w * 1.24} height={box.y + box.h - gy} fill={ground} />
      </g>
      {/* PROCEDURAL SET (REAL-ANIMATION §6): classroom/lab/street plates keyed
          off the beat's words; indoor sets wall over the outdoor planes. */}
      <SetLayer kind={beat.background.set ?? "meadow"} box={box} gy={gy} camX={camX} t={t} />
      {/* atmosphere — seeded motes rising between planes (secondary action) */}
      <g opacity={0.5}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
          const fx = ((i * 0.131 + 0.07) % 1);
          const near = i % 3 === 0;
          const speed = near ? 0.028 : 0.016;
          const fy = 1 - ((t * speed + i * 0.129) % 1);
          const mx = box.x + fx * box.w + Math.sin(t * 0.5 + i * 1.7) * box.w * 0.012 + camX * (near ? 0.7 : 0.3);
          const my = box.y + fy * box.h;
          return <circle key={i} cx={mx} cy={my} r={box.h * (near ? 0.004 : 0.0024)} fill="#ffffff" opacity={near ? 0.55 : 0.3} />;
        })}
      </g>
    </g>
  );
}

export function StoryStage({ beat, box, theme, t }: { beat: StoryBeat; box: Box; theme: Theme; t: number }) {
  const u = Math.min(box.w, box.h) / 46;
  return (
    <g>
      <Background beat={beat} box={box} t={t} />
      {beat.cast.map((m, mi) => {
        const charH = box.h * 0.6 * m.stage.scale;
        const charW = charH * 0.62;
        const facing = (m.facing ?? 1) as -1 | 1;
        // WALK-IN: enters from offstage (behind their facing) along an eased path
        // with a stepping bob — an animated-film entrance, not a materialization.
        const wp = m.walkIn ? easeOutQuint(clamp01(t / 0.9)) : 1;
        const startX = facing === 1 ? -0.14 : 1.14;
        const posAt = (tt: number) => {
          const w = m.walkIn ? easeOutQuint(clamp01(tt / 0.9)) : 1;
          return box.x + (m.walkIn ? startX + (m.stage.x - startX) * w : m.stage.x) * box.w;
        };
        const mx = m.walkIn ? startX + (m.stage.x - startX) * wp : m.stage.x;
        const stepBob = m.walkIn && wp < 1 ? Math.abs(Math.sin(wp * Math.PI * 4)) * (1 - wp) * box.h * 0.01 : 0;
        const cx = box.x + mx * box.w;
        const feetBase = box.y + m.stage.y * box.h;
        const feetY = feetBase - stepBob;
        const cbox: Box = { x: cx - charW / 2, y: feetY - charH, w: charW, h: charH, cx, cy: feetY - charH / 2 };
        const walking = !!m.walkIn && wp < 0.96 && t < 0.85;
        // Lip-sync (REAL-ANIMATION §5): the NARRATION (what the voiceover reads)
        // drives the mouth for the whole beat when provided; a bare dialogue
        // line falls back to reading-speed pacing (~12 chars/s).
        const speakStart = m.walkIn ? 0.85 : 0.15;
        const speech = m.speech ?? (m.dialogue
          ? { text: m.dialogue, start: speakStart, end: speakStart + Math.max(0.8, Math.min(m.dialogue.length / 12, 6)) }
          : undefined);
        // Current gesture (for effect triggers) — last acting key, else the base.
        let cur = m.reaction;
        let segStart = 0;
        if (m.acting) for (const k of m.acting) if (k.at <= t) { cur = k.reaction; segStart = k.at; }
        return (
          <g key={m.charId}>
            {/* Motion MULTIPLE (classic 2D smear): a ghost trailing a fast entrance. */}
            {walking && t > 0.08 && (
              <g opacity={0.22}>
                <CharacterActor spec={m.spec} reaction={m.reaction} acting={m.acting} speech={speech} groundY={feetBase - charH * 0.03}
                  box={{ ...cbox, x: posAt(t - 0.055) - charW / 2, cx: posAt(t - 0.055) }}
                  theme={theme} t={Math.max(0, t - 0.055)} facing={facing} seed={mi + 1} />
              </g>
            )}
            {/* Footfall dust while walking in (step cadence ≈ the bob). */}
            {m.walkIn && [0.18, 0.42, 0.66].map((at, i) => (
              <DustPuff key={`d${i}`} x={posAt(at)} y={feetBase} at={at} t={t} u={u} seed={mi * 7 + i} />
            ))}
            {/* Landing dust on celebrate jumps (landings every ~0.79s of the gesture). */}
            {cur === "celebrate" && [1, 2, 3].map((k) => (
              <DustPuff key={`c${k}`} x={cx} y={feetBase} at={segStart + k * (Math.PI / 4)} t={t} u={u} seed={mi * 11 + k} />
            ))}
            <SpeedLines x={cx - facing * charW * 0.5} y={feetY - charH * 0.45} t={t} u={u} dir={facing} active={walking} />
            <CharacterActor spec={m.spec} reaction={m.reaction} acting={m.acting} speech={speech} groundY={feetBase - charH * 0.03} box={cbox} theme={theme} t={t} facing={facing} seed={mi + 1} />
          </g>
        );
      })}
      {beat.cast.map((m, i) => {
        if (!m.dialogue) return null;
        const cx = box.x + m.stage.x * box.w;
        const headY = box.y + m.stage.y * box.h - box.h * 0.6 * m.stage.scale;
        // A walking character speaks AFTER arriving at their mark.
        const start = m.walkIn ? 0.85 : 0.15;
        const appear = smooth(clamp01((t - start) / 0.5));
        return (
          <g key={`b-${i}`}>
            <Bubble x={cx} y={headY - u * 0.5} text={m.dialogue} theme={theme} u={u} appear={appear} />
            {/* An exclamation earns an impact star as the bubble settles. */}
            {/!/.test(m.dialogue) && <ImpactStar x={cx + box.w * 0.13} y={headY - box.h * 0.075} at={start + 0.4} t={t} u={u} />}
          </g>
        );
      })}
    </g>
  );
}

// ── STORYLINE MODE (STORYLINE.md §1, §6) ─────────────────────────────────────
// Storyline is a committed RENDER MODE over the same beats: each beat becomes the
// recurring cast member delivering its narration on a stage, reusing the existing
// caption / voice / export pipeline (so preview === export and cost is unchanged).

/** Pick a reaction from the beat's mood so the presenter ACTS the line. */
export function moodReaction(text: string): ReactionName {
  const s = (text ?? "").toLowerCase();
  if (/\?|\bwonder|\bwhy\b|\bhow\b|what if|imagine|curious/.test(s)) return "think";
  if (/amazing|incredible|\bwow\b|great|success|\bwin\b|breakthrough|congrat|celebrate|finally|awesome/.test(s)) return "celebrate";
  if (/careful|mistake|warning|danger|problem|\bfail|wrong|avoid|risk|worse|sadly|unfortunately/.test(s)) return "sad";
  if (/\blook\b|\bhere\b|\bsee\b|notice|this is|\bcheck\b|first,|next,|then,|\bstep\b/.test(s)) return "point";
  if (/hello|welcome|let's|lets |let us|\bstart|\bbegin|\btoday\b|meet /.test(s)) return "wave";
  if (/\bbig\b|\bhuge\b|shock|surprising|\bnever\b|\balways\b|everyone|billion|million/.test(s)) return "shocked";
  if (/great job|well done|you got|nice|good|thumbs/.test(s)) return "thumbsup";
  return "talk";
}

/**
 * PERFORMANCE ACTING (REAL-ANIMATION §1): split the narration into phrases and
 * give each phrase its own gesture, timed to where the phrase sits in the beat —
 * the character ACTS the line ("spreads arms on 'huge', leans in on the
 * question") instead of holding one pose. Deterministic, $0 — the same cue
 * classifier as moodReaction, sequenced.
 */
export interface ActingKey { at: number; reaction: ReactionName }

export function deriveActing(narration: string, dur: number): ActingKey[] {
  const text = (narration ?? "").trim();
  if (!text) return [{ at: 0, reaction: "talk" }];
  // Phrase boundaries: sentences first, then long comma clauses.
  const phrases = text
    .split(/(?<=[.!?])\s+|,\s+(?=\w{4,})/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (phrases.length === 0) return [{ at: 0, reaction: "talk" }];
  const total = phrases.reduce((s, p) => s + p.length, 0) || 1;
  const keys: ActingKey[] = [];
  let acc = 0;
  for (const p of phrases) {
    const at = (acc / total) * Math.max(0.001, dur);
    const reaction = moodReaction(p);
    // Collapse adjacent duplicates — a held pose IS the moving hold.
    if (keys.length === 0 || keys[keys.length - 1].reaction !== reaction) {
      keys.push({ at, reaction });
    }
    acc += p.length;
  }
  if (keys[0].at !== 0) keys[0] = { ...keys[0], at: 0 };
  return keys;
}

/**
 * SHOT GRAMMAR (REAL-ANIMATION §2): cinematography per beat, on the existing
 * camera math. Wide to establish (the bubble lands), ease into a close-up on
 * the SPEAKER for the heart of the line, re-widen before the cut. Walk-in
 * beats hold the wide so the entrance plays. Odd beats frame a touch looser
 * than even ones (shot-size variety); an exclamation earns a tighter push.
 */
export function deriveShots(sb: StoryBeat, box: Box, dur: number, index: number): CameraTrack {
  const wide = { cx: box.cx, cy: box.cy, zoom: 1 };
  if (sb.cast.some((c) => c.walkIn) || dur < 3.2) return [{ t: 0, ...wide }];

  const speaker = sb.cast.find((c) => c.dialogue) ?? sb.cast[0];
  if (!speaker) return [{ t: 0, ...wide }];

  // Frame the speaker's head + bubble: a box around the upper body.
  const sx = box.x + speaker.stage.x * box.w;
  const headTop = box.y + speaker.stage.y * box.h - box.h * 0.6 * speaker.stage.scale;
  const target: Box = {
    x: sx - box.w * 0.3, y: headTop - box.h * 0.14,
    w: box.w * 0.6, h: box.h * 0.56,
    cx: sx, cy: headTop + box.h * 0.14,
  };
  const excl = sb.cast.some((c) => /!/.test(c.dialogue ?? ""));
  const tight = focusBox(target, box, index % 2 === 0 ? 1.08 : 1.22);
  const zoom = Math.min(excl ? 1.55 : 1.45, tight.zoom);
  const close = { cx: tight.cx, cy: tight.cy, zoom };

  return [
    { t: 0, ...wide },
    { t: 1.0, ...wide },
    { t: 1.9, ...close },                       // ease into the speaker
    { t: Math.max(2.0, dur - 1.1), ...close },  // moving hold
    { t: Math.max(2.4, dur - 0.35), ...wide },  // re-establish before the cut
  ];
}

/** A deterministic recurring cast (1 presenter) seeded from the topic — identity. */
export function buildCast(seed: string): CharacterSpec[] {
  const keys = Object.keys(CHARACTER_PRESETS);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  // Two recurring, visually-distinct avatars → a conversational skit. (Stick stays
  // available via spec.) The avatar family is the default richer look.
  const a = keys[h % keys.length];
  const b = keys[(h + 1) % keys.length];
  return [
    { ...CHARACTER_PRESETS[a], family: "avatar" },
    { ...CHARACTER_PRESETS[b], family: "avatar" },
  ];
}

/** The non-speaking character mirrors the speaker's mood so the skit feels alive. */
function listenerReaction(speaker: ReactionName): ReactionName {
  if (speaker === "celebrate" || speaker === "thumbsup") return "happy";
  if (speaker === "sad") return "sad";
  if (speaker === "shocked") return "shocked";
  if (speaker === "think") return "think";
  return "nod";
}

const STORY_BG: { sky: string; ground: string }[] = [
  { sky: "#cfe4ff", ground: "#bfe3b0" },
  { sky: "#ffe1c4", ground: "#d7c39a" },
  { sky: "#e7dbff", ground: "#c9c0e6" },
  { sky: "#d6f0ee", ground: "#bfe0d2" },
];

/**
 * Map a semantic beat → a StoryBeat. Hooks/CTAs get a single centred presenter;
 * body/recap beats are a TWO-HANDER — one character speaks (bubble + mood), the
 * other reacts, with the speaker ALTERNATING so it reads as a conversation.
 */
export function deriveStoryBeat(beat: { title: string; narration?: string; role?: string; dur?: number }, cast: CharacterSpec[], index: number, set?: SetKind): StoryBeat {
  const speak = moodReaction(`${beat.title} ${beat.narration ?? ""}`);
  const bg = STORY_BG[index % STORY_BG.length];
  // The SET (REAL-ANIMATION §6): held story-wide when the caller derived one
  // from the WHOLE script (continuity — the stage shouldn't teleport between
  // beats); a lone beat falls back to its own words.
  const background = { kind: "flat" as const, sky: bg.sky, ground: bg.ground, set: set ?? deriveSet(`${beat.title} ${beat.narration ?? ""}`) };
  // The SPEAKER performs the line phrase-by-phrase (REAL-ANIMATION §1).
  const dur = beat.dur ?? 6;
  const acting = deriveActing(beat.narration || beat.title, dur);
  const walkIn = index === 0;
  // Lip-sync spans the NARRATION the voiceover actually reads, across the whole
  // beat — the mouth must not clamp shut while the audio keeps speaking.
  const speakStart = walkIn ? 0.85 : 0.15;
  const speech: Speech = { text: beat.narration || beat.title, start: speakStart, end: Math.max(speakStart + 0.8, dur - 0.45) };

  // The FIRST beat opens like a film: the cast WALKS IN before speaking.
  const single = cast.length < 2 || beat.role === "hook" || beat.role === "cta";
  if (single) {
    return {
      background,
      cast: [{ charId: "narrator", spec: cast[0], stage: { x: 0.5, y: 0.99, scale: 1 }, reaction: speak, acting, facing: index % 2 === 0 ? 1 : -1, dialogue: beat.title, speech, walkIn }],
    };
  }

  // Two-hander: left = cast[0] (faces right), right = cast[1] (faces left).
  const listen = listenerReaction(speak);
  const leftSpeaks = index % 2 === 0;
  return {
    background,
    cast: [
      { charId: "a", spec: cast[0], stage: { x: 0.3, y: 0.99, scale: leftSpeaks ? 0.96 : 0.86 }, facing: 1, reaction: leftSpeaks ? speak : listen, acting: leftSpeaks ? acting : undefined, dialogue: leftSpeaks ? beat.title : undefined, speech: leftSpeaks ? speech : undefined, walkIn },
      { charId: "b", spec: cast[1], stage: { x: 0.72, y: 0.98, scale: leftSpeaks ? 0.86 : 0.96 }, facing: -1, reaction: leftSpeaks ? listen : speak, acting: leftSpeaks ? undefined : acting, dialogue: leftSpeaks ? undefined : beat.title, speech: leftSpeaks ? undefined : speech, walkIn },
    ],
  };
}

/** The story-wide SET: derived once from the WHOLE script so the stage holds
 *  its location across beats (continuity — same pattern as buildCast). */
export function deriveStorySet(beats: { title: string; narration?: string }[]): SetKind {
  return deriveSet(beats.map((b) => `${b.title} ${b.narration ?? ""}`).join(" "));
}

/** A SceneDef that renders a beat as a Storyline skit (stage + reused captions). */
export function beatToStoryScene(beat: Beat, meta: { index: number; total: number; format: string; set?: SetKind }, cast: CharacterSpec[]): SceneDef {
  return {
    id: beat.id,
    label: beat.role,
    format: meta.format,
    dur: beat.dur,
    render: (ctx) => {
      const { layout, theme, t } = ctx;
      const capTop = layout.caption.y;
      const storyBox: Box = { x: layout.full.x, y: layout.full.y, w: layout.full.w, h: capTop - layout.full.y, cx: layout.full.cx, cy: (layout.full.y + capTop) / 2 };
      const sb = deriveStoryBeat(beat, cast, meta.index, meta.set);
      // Cinematography (REAL-ANIMATION §2): sample the beat's shot list and film
      // the stage through it. Captions stay OUTSIDE the shot — burned-in UI.
      const cam = cameraAt(deriveShots(sb, storyBox, beat.dur, meta.index), t);
      const shotT = cameraTransformCentered(cam, storyBox.cx, storyBox.cy);
      const clipId = `shot-${beat.id.replace(/[^a-z0-9_-]/gi, "").slice(0, 24)}`;
      return (
        <>
          <defs>
            <clipPath id={clipId}><rect x={storyBox.x} y={storyBox.y} width={storyBox.w} height={storyBox.h} /></clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <g transform={shotT}>
              <StoryStage beat={sb} box={storyBox} theme={theme} t={t} />
            </g>
          </g>
          <Caption box={layout.caption} segs={beat.captions} t={t} theme={theme} unit={layout.unit} />
        </>
      );
    },
  };
}

/**
 * STORYBOOK sub-mode (STORYLINE.md §11): each beat is a full-bleed GENERATED
 * scene still (Ken-Burns) + subtitles — no moving puppets. Reuses the asset the
 * pipeline already fetched (beat.visual.assetUri); shows a themed plate until it
 * lands. Captions reused, so preview === export.
 */
export function beatToStorybookScene(beat: Beat, meta: { index: number; total: number; format: string }): SceneDef {
  return {
    id: beat.id,
    label: beat.role,
    format: meta.format,
    dur: beat.dur,
    render: (ctx) => {
      const { layout, theme, t } = ctx;
      const href = (beat.visual as { assetUri?: string }).assetUri;
      const full = layout.full;
      const P = theme.palette;
      return (
        <>
          {href ? (
            <StoryStage beat={{ background: { kind: "image", href }, cast: [] }} box={full} theme={theme} t={t} />
          ) : (
            <g>
              <rect x={full.x} y={full.y} width={full.w} height={full.h} fill={P.surface} />
              <text x={full.cx} y={full.cy} textAnchor="middle" fontFamily={theme.title.family} fontWeight={700} fontSize={layout.unit * 3} fill={P.muted}>{beat.title}</text>
              <text x={full.cx} y={full.cy + layout.unit * 4} textAnchor="middle" fontFamily={theme.body.family} fontSize={layout.unit * 1.8} fill={P.muted} opacity={0.7}>illustrating…</text>
            </g>
          )}
          <Caption box={layout.caption} segs={beat.captions} t={t} theme={theme} unit={layout.unit} />
        </>
      );
    },
  };
}

/**
 * GREEN-SCREEN OVERLAY mode: chroma green (#00FF00) + the caption engine + the
 * voiceover — nothing else. Paste a script → export → chroma-key the result over
 * ANY footage in any editor (CapCut/Premiere/Resolve). Full-frame green (covers
 * the theme background), captions keep the selected theme's caption styling so
 * the overlay matches the creator's look. $0 — it's the same deterministic path.
 */
export function beatToOverlayScene(beat: Beat, meta: { index: number; total: number; format: string }): SceneDef {
  return {
    id: beat.id,
    label: beat.role,
    format: meta.format,
    dur: beat.dur,
    render: (ctx) => {
      const { layout, theme, t } = ctx;
      const full = layout.full;
      return (
        <>
          <rect x={full.x - 4} y={full.y - 4} width={full.w + 8} height={full.h + 8} fill="#00FF00" />
          <Caption box={layout.caption} segs={beat.captions} t={t} theme={theme} unit={layout.unit} />
        </>
      );
    },
  };
}

/** Convenience: a 2-hander conversation beat from two specs + their lines. */
export function twoHander(a: CharacterSpec, aLine: string, aReact: ReactionName, b: CharacterSpec, bLine: string, bReact: ReactionName, bg?: StoryBeat["background"]): StoryBeat {
  return {
    background: bg ?? { kind: "flat" },
    cast: [
      { charId: "a", spec: a, stage: { x: 0.27, y: 0.98, scale: 1 }, reaction: aReact, facing: 1, dialogue: aLine },
      { charId: "b", spec: b, stage: { x: 0.73, y: 0.98, scale: 1 }, reaction: bReact, facing: -1, dialogue: bLine },
    ],
  };
}
